import * as _ from 'lodash';
import * as moment from 'moment-timezone';
import { Component, ElementRef, Input, QueryList, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChildren } from '@angular/core';
import { Router } from '@angular/router';
import { Challenge, ChallengeService, Team, TeamMembershipStatus, TeamUser } from 'app/shared/services/challenge.service';
import { UserService } from 'app/shared/services/user.service';
import { Subject } from 'rxjs';
import { User } from 'app/shared/models/User';
import { MatSnackBar } from '@angular/material';
import { TranslateService } from '@ngx-translate/core';

/**
 * Component to displayed leaderboard of a challenge
 */
@Component({
  selector: 'app-challenge-leaderboard',
  templateUrl: './challenge-leaderboard.component.html',
  styleUrls: ['./challenge-leaderboard.component.scss'],
})
export class ChallengeLeaderboardComponent implements OnInit, OnDestroy, OnChanges {
  // The displayed challenge
  @Input() challenge: Challenge;

  // Dom elements of all users
  @ViewChildren('userElem') allUsers: QueryList<ElementRef>;

  // Dom elements of all teams
  @ViewChildren('teamElem') allTeams: QueryList<ElementRef>;

  // List of teams for this challenge
  public teams: Team[];

  // The team of the challenge, if the user is member of the team
  public team: Team;

  // The currently logged in user
  public currentUser: User;

  // The currently logged in user object in team
  public teamUser: TeamUser;

  // List of unfiltered teams for this challenge
  public unfilteredTeams: Team[];

  // The member status of current user (before filtering teams)
  public originalTeamUser: TeamUser;

  // Whether challenge lies in the past
  public isPastChallenge: boolean;

  public teamHasNotEnoughMembers = false;

  // Current day of challenge (Required to check if daily bonus was already granted for today)
  public currentChallengeDay: number;

  // The maximum week the user should be able to navigate to
  public maxWeek: number;

  // Selected week in overview for users team
  public selectedUserWeek: number = 0;

  // Selected week in overview of all teams
  public selectedTeamWeek: number = 0;

  public showWeeklyUserScore = false;

  public showWeeklyTeamScore = false;

  public challengeWinnerTeams: {
    first: Team;
    second: Team;
    third: Team;
  } = {
    first: null,
    second: null,
    third: null,
  };

  // Object containing weeks as keys and weekly winner team as values
  public weeklyWinners = {};

  // Reference to TeamMembershipStatus to be able to use it in template
  public TeamMembershipStatus: typeof TeamMembershipStatus = TeamMembershipStatus;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private userService: UserService,
    private challengeService: ChallengeService,
    private translate: TranslateService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.userService.currentUser.takeUntil(this.ngUnsubscribe).subscribe((user) => {
      if (user) {
        this.currentUser = user;
        this.setChallengeStatus();
        this.setTeam();
        this.scrollIntoView();
      }
    });

    // Trigger team reload in order for points to update
    this.challengeService.reloadTeams();

    this.challengeService.teams.takeUntil(this.ngUnsubscribe).subscribe((teams) => {
      if (!teams) {
        return;
      }

      this.unfilteredTeams = teams;

      // Exclude teams which haven't reached the required minimum amount of members to not show them in scoring board
      if (this.challenge.minimalTeamMembers) {
        teams = teams.filter((team) => {
          const teamMembers = team.users.filter((user) => user.membershipStatus === TeamMembershipStatus.MEMBER);
          return teamMembers.length >= this.challenge.minimalTeamMembers;
        });
      }

      this.teams = teams;
      this.setTeam();
      this.buildWeeklyWinners();
      this.setWinnerTeams();
      this.scrollIntoView();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes && this.challenge) {
      this.setChallengeStatus();
      this.initializeFilter();
    }
  }

  /**
   * Scrolls within user and team containers, so that user can see the himself and his team in the list
   */
  public scrollIntoView(): void {
    // Scroll to current user
    if (this.currentUser && this.allUsers && this.allUsers.length > 0) {
      const element = this.allUsers.find((userElement) => {
        return userElement.nativeElement.dataset.userid === this.currentUser._id;
      });
      element.nativeElement.scrollIntoView(false);
    }

    // Scroll to current team
    if (this.team && this.allTeams && this.allTeams.length > 0) {
      const element = this.allTeams.find((teamElement) => {
        return teamElement.nativeElement.dataset.teamid === this.team._id;
      });
      element.nativeElement.scrollIntoView(false);
    }
  }

  /**
   * Toggle between total score and weekly score view in user list
   * @param showScore - Whether weekly score should be displayed or totalScore
   */
  public setShowWeeklyUserScore(showWeeklyScore: boolean): void {
    this.showWeeklyUserScore = showWeeklyScore;
    this.sortUsers();
  }

  /**
   * Toggle between total score and weekly score view in team list
   * @param showScore - Whether weekly score should be displayed or totalScore
   */
  public setShowWeeklyTeamScore(showWeeklyScore: boolean): void {
    this.showWeeklyTeamScore = showWeeklyScore;
    this.sortTeams();
  }

  /**
   * Sets the selected week
   * @param week - The week that should be set
   * @param view - Whether week should be changed in "myTeam" section or in "all teams" section
   */
  public setSelectedWeek(week: number, view: 'users' | 'teams') {
    const lastDay = this.localizedDaysPassed(this.challenge.start, this.challenge.end, this.currentUser.timeZone);
    const maxWeek = Math.trunc(lastDay / 7);

    if (week >= 0 && week <= maxWeek) {
      if (view === 'users') {
        this.selectedUserWeek = week;
        this.sortUsers();
      }
      if (view === 'teams') {
        this.selectedTeamWeek = week;
        this.sortTeams();
      }
    }
  }

  /**
   * Set whether challenge is in past or not
   */
  private setChallengeStatus() {
    if (!this.challenge || !this.currentUser) {
      return;
    }

    this.isPastChallenge = moment(this.challenge.end).isBefore(moment());
    this.setWinnerTeams();
  }

  private setWinnerTeams() {
    // Build leaderboard with challenge winners
    if (this.isPastChallenge && this.teams) {
      const teamsCopy = [...this.teams];
      const sortedByTotalScore = teamsCopy.sort((a, b) => {
        return (a.points.total || 0) > (b.points.total || 0) ? -1 : 1;
      });
      if (sortedByTotalScore.length > 0) {
        this.challengeWinnerTeams.first = sortedByTotalScore[0];
      } else {
        this.challengeWinnerTeams.first = null;
      }
      if (sortedByTotalScore.length > 1) {
        this.challengeWinnerTeams.second = sortedByTotalScore[1];
      } else {
        this.challengeWinnerTeams.second = null;
      }
      if (sortedByTotalScore.length > 2) {
        this.challengeWinnerTeams.third = sortedByTotalScore[2];
      } else {
        this.challengeWinnerTeams.third = null;
      }
    }
  }

  /**
   * Selects the current week at component initialization
   */
  private initializeFilter() {
    if (!this.currentUser || !this.challenge || !this.teams) {
      return;
    }

    const currentDay = this.localizedDaysPassed(this.challenge.start, moment(), this.currentUser.timeZone);
    this.currentChallengeDay = currentDay;

    const currentWeek = Math.trunc(currentDay / 7);

    const lastDay = this.localizedDaysPassed(this.challenge.start, this.challenge.end, this.currentUser.timeZone);
    const maxWeek = Math.trunc(lastDay / 7);

    this.maxWeek = maxWeek;
    // If current week is larger than maxWeek, that means the challenge has already ended.
    // In this case initialize to last week of challenge
    const weekToSet = currentWeek > maxWeek ? maxWeek : currentWeek;
    this.setSelectedWeek(weekToSet, 'users');
    this.setSelectedWeek(weekToSet, 'teams');
  }

  /**
   * Sort team members based on scored points in current selected week
   */
  private sortUsers() {
    if (!this.team || !this.team.userGroups || !this.team.userGroups.members) {
      return;
    }

    if (this.showWeeklyUserScore) {
      this.team.userGroups.members.sort((a, b) => {
        return (a.points.byWeek[this.selectedUserWeek] || 0) > (b.points.byWeek[this.selectedUserWeek] || 0) ? -1 : 1;
      });
    } else {
      this.team.userGroups.members.sort((a, b) => {
        return (a.points.total || 0) > (b.points.total || 0) ? -1 : 1;
      });
    }
  }

  /**
   * Sort teams based on scored points in current selected week
   */
  private sortTeams() {
    if (!this.teams) {
      return;
    }
    if (this.showWeeklyTeamScore) {
      this.teams.sort((a, b) => {
        return (a.points.byWeek[this.selectedTeamWeek] || 0) > (b.points.byWeek[this.selectedTeamWeek] || 0) ? -1 : 1;
      });
    } else {
      this.teams.sort((a, b) => {
        return (a.points.total || 0) > (b.points.total || 0) ? -1 : 1;
      });
    }
  }

  /**
   * Sets membership status of user which has effect on the available actions
   */
  private setTeam(): void {
    if (!this.teams || !this.currentUser) {
      this.team = undefined;
      this.teamUser = undefined;
      return;
    }

    this.originalTeamUser = null;
    const originalTeam = this.unfilteredTeams.find((team) => {
      for (const user of team.users) {
        if (user._id === this.currentUser._id) {
          this.originalTeamUser = user;
          return true;
        }
      }
    });

    if (
      this.originalTeamUser &&
      this.originalTeamUser.membershipStatus === TeamMembershipStatus.MEMBER &&
      originalTeam.userGroups.members.length < this.challenge.minimalTeamMembers
    ) {
      this.teamHasNotEnoughMembers = true;
    } else {
      this.teamHasNotEnoughMembers = false;
    }

    let teamUser = null;
    // If teams are returned, set team the user belongs to
    // Team is only set for members, not if user has invitation or application status
    this.team = this.teams.find((team) => {
      for (const user of team.users) {
        if (user._id === this.currentUser._id) {
          teamUser = user;
          return true;
        }
      }
    });

    if (!this.team) {
      this.teamUser = null;
    } else {
      this.teamUser = teamUser;
    }

    if (this.team) {
      this.initializeFilter();
    }
  }

  /**
   * Build structure to make weekly winners more easily accessible.
   */
  public buildWeeklyWinners() {
    this.weeklyWinners = {};
    if (this.teams) {
      for (const team of this.teams) {
        for (let i = 0; i < team.bonuses.weeklyWinner.weeks.length; ++i) {
          this.weeklyWinners[team.bonuses.weeklyWinner.weeks[i]] = team._id;
        }
      }
    }
  }

  /**
   * Returns difference in days between start and end date in local timezone
   * @param startDate - the earlier date
   * @param endDate - the later date
   * @param timeZone - timeZone of user
   */
  public localizedDaysPassed(startDate, endDate, timeZone) {
    // Example:
    // Challenge start is 2020-02-30 23:00 UTC and now is 2020-02-31 03:00

    // In UTC, a new day has started since challenge start
    // In UTC +1 timezone however, both timestamps are at the same day.
    // To allow user timezone differences and still be displayed as the same day,
    // bonuses and points are stored by day number instead of dates and refer to day in users timezone

    const end = moment(endDate).tz(timeZone).endOf('day');
    const start = moment(startDate).tz(timeZone).startOf('day');

    return end.diff(start, 'days');
  }

  /** Debounce function to reduce email spam */
  public cheerForTeamMemberDebounced: (name: string, userID: string) => void = _.throttle(
    (name: string, userID: string) => {
      this.cheerForTeamMember(name, userID);
    },
    5000,
    { leading: true, trailing: false },
  );

  /**
   * A user can cheer for other team members to send them a motivation message.
   */
  public cheerForTeamMember(name: string, userID: string) {
    this.snackBar.open(this.translate.instant('STATS.CHALLENGE_LEADERBOARD.cheerToast', { userName: name }), null, {
      verticalPosition: 'bottom',
      duration: 4000,
      panelClass: ['b2a-success', 'bottom'],
    });

    this.challengeService.cheerForTeamMember(this.challenge._id, this.team._id, userID).then();
  }

  public openChallengeTutorial(): void {
    this.router.navigate(['challengeTutorial']).then();
  }

  /**
   * On destroy cancel active subscriptions
   */
  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/theme/components/challenge-statistics/components/challenge-leaderboard/challenge-leaderboard.component.ts